import { useQuery } from '@tanstack/react-query';
import {
  type ConnectorPlatform,
  CONNECTOR_PLATFORM_LABELS,
  listPlatformsQueryOptions,
} from '@/entities/media-connector';

// Derive the valid set from the canonical label map (single source of truth) so a
// newly added platform is accepted here automatically — no parallel list to update.
const VALID_PLATFORMS = Object.keys(CONNECTOR_PLATFORM_LABELS) as ConnectorPlatform[];

export interface UseMediaConnectorAvailabilityResult {
  isValidPlatform: boolean;
  platformsPending: boolean;
  isPlatformEnabled: boolean;
}

/**
 * Whether a platform may be shown at all. Deliberately separate from
 * `useMediaConnectorDetail`: this runs only the platform-list query, so the
 * always-mounted shell can answer "is there anything to render?" without
 * mounting the account list or the 30s sync-status poll behind it.
 *
 * Unknown and flag-disabled platforms are indistinguishable by design — the
 * backend filters the list by each platform's feature flag, so a disabled
 * platform is simply absent and must not leak that it exists.
 */
export function useMediaConnectorAvailability(
  platform: string | null,
): UseMediaConnectorAvailabilityResult {
  const { data: platformsData, isPending: platformsPending } = useQuery(
    listPlatformsQueryOptions(),
  );

  return {
    isValidPlatform: platform !== null && VALID_PLATFORMS.includes(platform as ConnectorPlatform),
    platformsPending,
    isPlatformEnabled: (platformsData?.items ?? []).some((p) => p.platform === platform),
  };
}
